import { createStore, createEffect, createEvent, combine } from "effector"
import dayjs, { Dayjs } from "utils/dayjs"
import { sum } from "lodash"
import { AxiosError } from "axios"

import ApiService from "services/ApiService"
import { ApiServiceName, ApiLinkEntity, ApiLinkRel, EditSessionDuration, EditSessionConfirmModalDuration, ErrorCode, LoadingListFormat } from "config/constants"
import CustomError from "models/CustomError"
import { downloadUrlAsFile } from "utils/utils"
import UserService from "services/UserService"
import { Guid, IApiLink } from "@/types"
import ShippingOrderCart, { ICreateShippingOrderCartDto, IGetShippingOrderCartDto, IUpdateShippingOrderCartDto } from "models/ShippingOrderCart"
import { IUpdateShippingOrderCartAssetDto } from "models/ShippingOrderCartAsset"
import unitsStore from "./units"
import { ICreateShippingOrderCartTransportDto, IUpdateShippingOrderCartTransportDto } from "models/ShippingOrderCartTransport"
import { ShippingOrderStatus } from "models/ShippingOrder"
import ErrorHandler from "utils/errorHandler"
import { ICreateShippingOrderCartManualPositionDto, IUpdateShippingOrderCartManualPositionDto } from "models/ShippingOrderCartManualPosition"
import RequestLocation, { ICreateRequestLocation, IGetRequestLocationDto } from "models/RequestLocation"
import ShippingOrderCartSegment, { IUpdateShippingOrderCartSegment } from "models/ShippingOrderCartSegment"

type SessionTimeout = NodeJS.Timeout | number

export enum ShippingOrderCartItemsSelectionState {
    noneSelected = "noneSelected",
    allSelected = "allSelected",
    someSelected = "someSelected",
}


// Stores
const $cart = createStore<ShippingOrderCart | null>(null)
const $sessionTimeoutId = createStore<SessionTimeout | null>(null)
const $showContinueSessionModal = createStore<boolean>(false)
const $isUpdateCartPending = createStore<boolean>(false)

// Events
const setShowContinueSessionModal = createEvent<boolean>()
const reset = createEvent()

// Effects
const createCart = createEffect<ICreateShippingOrderCartDto, ShippingOrderCart, CustomError<{ email: string, date: Dayjs | null } | undefined>>(async dto => {
    setShowContinueSessionModal(false)

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.request({
            serviceName: ApiServiceName.asset,
            followLink: {
                entity: ApiLinkEntity.shippingOrderCart,
                rel: ApiLinkRel.new,
            },
            data: dto,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        if (!e.isAxiosError) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }

        const error = e as AxiosError<any>

        if (error.response && (error.response.status === 403 || error.response.status === 409 || error.response.status === 423)) {
            const userEmailLink = error.response.data.links ? error.response.data.links.find((lnk: IApiLink) => lnk.entity === ApiLinkEntity.userMail && lnk.rel === ApiLinkRel.self) : null
            let email = ""
            if (userEmailLink) {
                email = (await UserService.getUserEmailByLink(userEmailLink)) || ""
            }
            let code = ErrorCode.systemError
            switch (error.response.status) {
                case 403:
                    code = ErrorCode.forbidden
                    break
                case 409:
                    code = ErrorCode.conflict
                    break
            }
            throw new CustomError({
                code,
                payload: error.response.data ? {
                    email,
                    date: error.response.data.lu_date ? dayjs(error.response.data.lu_date) : null,
                } : undefined,
            })
        }

        throw new CustomError({
            code: ErrorCode.systemError
        })
    }
})

const updateCart = createEffect<IUpdateShippingOrderCartDto, ShippingOrderCart, CustomError>(async dto => {
    const linkUpdate = $cart.getState()?.linkUpdate

    if (!linkUpdate) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkUpdate,
            data: dto,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const touchCart = createEffect<void, void, CustomError>(async () => {
    const linkTouch = $cart.getState()?.linkTouch

    if (!linkTouch) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        await ApiService.followLink({
            link: linkTouch,
        })
    } catch (e) {
        if (e.isAxiosError) {
            const error = e as AxiosError<any>
            if (error.response && error.response.status === 403) {
                throw new CustomError({
                    code: ErrorCode.forbidden
                })
            }
        }

        throw new CustomError({
            code: ErrorCode.systemError
        })
    }
})

export interface IChangeShippingOrderCartStatusDto {
    status: ShippingOrderStatus
    comment?: string | null
}

const changeCartStatus = createEffect<IChangeShippingOrderCartStatusDto, ShippingOrderCart, CustomError>(async dto => {
    const linkChangeStatus = $cart.getState()?.linkChangeStatus

    if (!linkChangeStatus) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkChangeStatus,
            data: dto,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const deleteCart = createEffect<void, void, CustomError>(async () => {
    const linkDelete = $cart.getState()?.linkDelete

    if (!linkDelete) {
        return
    }

    try {
        await ApiService.followLink({
            link: linkDelete,
        })
    } catch (e) {

    }
})

interface IPublishShippingOrderCartParams {
    triggerShippingOrderChangedNotification?: boolean
}

const publishCart = createEffect<IPublishShippingOrderCartParams | void, Guid, CustomError>(async (params) => {
    const cart = $cart.getState()

    if (!cart?.id || !cart.linkPublish) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: { id: Guid } = await ApiService.followLink({
            link: cart.linkPublish,
            data: {
                shippingOrderCartId: cart.id,
                triggerShippingOrderChangedNotification: !!params?.triggerShippingOrderChangedNotification,
            }
        })
        return resp.id
    } catch (e) {
        console.log(e)
        ErrorHandler.handleHttpError(e)
    }
})

const addAssets = createEffect<Guid[], ShippingOrderCart, CustomError>(async assetIds => {
    const linkAddAssets = $cart.getState()?.linkAddAssets

    if (!linkAddAssets) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkAddAssets,
            data: assetIds,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const changeAssets = createEffect<IUpdateShippingOrderCartAssetDto, ShippingOrderCart, CustomError>(async dto => {
    const linkUpdateAsset = $cart.getState()?.linkUpdateAsset

    if (!linkUpdateAsset) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkUpdateAsset,
            data: dto,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const deleteAssets = createEffect<Guid[], ShippingOrderCart, CustomError>(async assetIds => {
    const linkDeleteAsset = $cart.getState()?.linkDeleteAssets

    if (!linkDeleteAsset) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkDeleteAsset,
            data: assetIds,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})


const createManualPositions = createEffect<{ link?: IApiLink, data: ICreateShippingOrderCartManualPositionDto[] }, ShippingOrderCart, CustomError>(async (params) => {
    const linkCreateManualPositions = params.link || $cart.getState()?.linkCreateManualPositions

    if (!linkCreateManualPositions) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkCreateManualPositions,
            data: params.data,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const updateManualPositions = createEffect<IUpdateShippingOrderCartManualPositionDto, ShippingOrderCart, CustomError>(async (dto) => {
    const linkUpdateManualPosition = $cart.getState()?.linkUpdateManualPosition

    if (!linkUpdateManualPosition) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkUpdateManualPosition,
            data: dto,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const deleteManualPosition = createEffect<Guid, ShippingOrderCart, CustomError>(async manualPositionId => {
    const linkDeleteManualPosition = $cart.getState()?.linkDeleteManualPosition

    if (!linkDeleteManualPosition) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkDeleteManualPosition,
            linkQuery: { ManualPositionId: manualPositionId },
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const createTransport = createEffect<ICreateShippingOrderCartTransportDto | void, ShippingOrderCart, CustomError>(async dto => {
    const linkCreateTransport = $cart.getState()?.linkCreateTransport
    const defaultMeasurementSystemId = unitsStore.$units.getState()[0]?.id

    if (!linkCreateTransport || !defaultMeasurementSystemId) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkCreateTransport,
            data: dto || {
                measurementSystemId: defaultMeasurementSystemId,
            },
        })

        const newCart = await prepareCartInstance(resp)
        return newCart
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const updateTransport = createEffect<IUpdateShippingOrderCartTransportDto, ShippingOrderCart, CustomError>(async dto => {
    const linkUpdateTransport = $cart.getState()?.linkUpdateTransport

    if (!linkUpdateTransport) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkUpdateTransport,
            data: dto,
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const deleteTransport = createEffect<Guid, ShippingOrderCart, CustomError>(async transportId => {
    const linkDeleteTransport = $cart.getState()?.linkDeleteTransport

    if (!linkDeleteTransport) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkDeleteTransport,
            linkQuery: { TransportId: transportId },
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})


const updateSegment = createEffect<IUpdateShippingOrderCartSegment, ShippingOrderCart, CustomError>(async data => {
    const linkUpdateSegment = $cart.getState()?.linkUpdateSegment

    if (!linkUpdateSegment) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkUpdateSegment,
            data: ShippingOrderCartSegment.toUpdateDto(data),
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

export interface IMoveShippingOrderCartSegment {
    segmentId: Guid
    sourceTransportId?: Guid
    destinationTransportId?: Guid
    sequence?: number
}

const moveSegment = createEffect<IMoveShippingOrderCartSegment, void, CustomError>(async params => {
    const {
        segmentId,
        sourceTransportId,
        destinationTransportId,
        // sequence,
    } = params

    const cart = $cart.getState()

    if (!sourceTransportId || !cart) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    const { assets, manualPositions } = cart.getTransportSegmentItems({
        transportId: sourceTransportId,
        segmentId,
    })
    const assetIds = assets.map(a => a.assetId)
    const manualPositionIds = manualPositions.map(mp => mp.id)

    try {
        if (assets.length) {
            await changeAssets({
                assetIds,
                transportId: destinationTransportId || null,
            })
        }
        if (manualPositions.length) {
            await updateManualPositions({
                ids: manualPositionIds,
                transportId: destinationTransportId || null,
            })
        }
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }

})

const importLoadingList = createEffect<Guid, ShippingOrderCart, CustomError>(async shippingOrderId => {
    const linkImportLoadingList = $cart.getState()?.linkImportLoadingList

    if (!linkImportLoadingList) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetShippingOrderCartDto = await ApiService.followLink({
            link: linkImportLoadingList,
            linkParams: {
                shippingOrderId,
            }
        })

        return await prepareCartInstance(resp)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

export interface IExportLoadingListParams {
    includeShippingNote?: boolean
    language?: string
    format?: LoadingListFormat
    includeSummary?: boolean
    ourContactPersonEmployeeId?: Guid
}

const exportLoadingList = createEffect<IExportLoadingListParams, void, CustomError>(async params => {
    const linkExportLoadingList = $cart.getState()?.linkExportLoadingList

    if (!linkExportLoadingList) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: { uri: string } = await ApiService.followLink({
            link: linkExportLoadingList,
            linkQuery: {
                includeShippingNote: params.includeShippingNote,
                language: params.language,
                format: params.format,
                includeSummary: params.includeSummary,
                ourContactPersonEmployeeId: params.ourContactPersonEmployeeId,
            },
        })

        downloadUrlAsFile(resp.uri)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

const downloadPickingList = createEffect<{ language: string }, void, CustomError>(async params => {
    const linkGetPickingListReport = $cart.getState()?.linkGetPickingListReport

    if (!linkGetPickingListReport) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: { uri: string } = await ApiService.followLink({
            link: linkGetPickingListReport,
            linkParams: {
                query: {
                    language: params.language,
                },
            },
        })

        downloadUrlAsFile(resp.uri)
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

export const getRequestLocations = createEffect<void, RequestLocation[], CustomError>(async () => {
    const linkGetRequestLocations = $cart.getState()?.linkGetRequestLocations

    if (!linkGetRequestLocations) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: IGetRequestLocationDto[] = await ApiService.followLink({
            link: linkGetRequestLocations,
        })

        return resp.map(dto => new RequestLocation(dto))
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

export const createRequestLocation = createEffect<ICreateRequestLocation, Guid, CustomError>(async data => {
    const linkCreateRequestLocation = $cart.getState()?.linkCreateRequestLocation

    if (!linkCreateRequestLocation) {
        throw new CustomError({
            code: ErrorCode.systemError
        })
    }

    try {
        const resp: { id: Guid } = await ApiService.followLink({
            link: linkCreateRequestLocation,
            data: RequestLocation.toCreateDto(data),
        })

        return resp.id
    } catch (e) {
        ErrorHandler.handleHttpError(e)
    }
})

// Listeners
$cart
    .on(createCart.doneData, (_, payload) => payload)
    .on(updateCart.doneData, (_, payload) => payload)
    .on(addAssets.doneData, (_, payload) => payload)
    .on(changeCartStatus.doneData, (_, payload) => payload)
    .on(changeAssets.doneData, (_, payload) => payload)
    .on(deleteAssets.doneData, (_, payload) => payload)
    .on(createManualPositions.doneData, (_, payload) => payload)
    .on(updateManualPositions.doneData, (_, payload) => payload)
    .on(deleteManualPosition.doneData, (_, payload) => payload)
    .on(createTransport.doneData, (_, payload) => payload)
    .on(updateTransport.doneData, (_, payload) => payload)
    .on(deleteTransport.doneData, (_, payload) => payload)
    .on(updateSegment.doneData, (_, payload) => payload)
    .on(importLoadingList.doneData, (_, payload) => payload)
    .on(publishCart.doneData, () => null)
    .on(deleteCart.finally, () => null)
    .on(reset, () => null)

$showContinueSessionModal
    .on(setShowContinueSessionModal, (_, payload) => payload)
    .on(touchCart.done, () => false)
    .on(reset, () => false)

$sessionTimeoutId
    .on(createCart.done, refreshSessionTimeout)
    .on(updateCart.done, refreshSessionTimeout)
    .on(touchCart.done, refreshSessionTimeout)
    .on(changeCartStatus.done, refreshSessionTimeout)
    .on(addAssets.done, refreshSessionTimeout)
    .on(changeAssets.done, refreshSessionTimeout)
    .on(deleteAssets.done, refreshSessionTimeout)
    .on(createManualPositions.done, refreshSessionTimeout)
    .on(updateManualPositions.done, refreshSessionTimeout)
    .on(deleteManualPosition.done, refreshSessionTimeout)
    .on(createTransport.done, refreshSessionTimeout)
    .on(updateTransport.done, refreshSessionTimeout)
    .on(deleteTransport.done, refreshSessionTimeout)
    .on(updateSegment.done, refreshSessionTimeout)
    .on(getRequestLocations.done, refreshSessionTimeout)
    .on(createRequestLocation.done, refreshSessionTimeout)
    .on(importLoadingList.done, refreshSessionTimeout)
    .on(reset, clearSessionTimeout)

const $cartUpdatesInFlightCount = combine([
    createCart.inFlight,
    updateCart.inFlight,
    changeCartStatus.inFlight,
    touchCart.inFlight,
    addAssets.inFlight,
    changeAssets.inFlight,
    deleteAssets.inFlight,
    createManualPositions.inFlight,
    updateManualPositions.inFlight,
    deleteManualPosition.inFlight,
    createTransport.inFlight,
    updateTransport.inFlight,
    deleteTransport.inFlight,
    updateSegment.inFlight,
    exportLoadingList.inFlight,
    downloadPickingList.inFlight,
    getRequestLocations.inFlight,
    createRequestLocation.inFlight,
    importLoadingList.inFlight,
], (counts) => sum(counts))

// Helpers
function clearSessionTimeout(timeoutId: SessionTimeout | null) {
    if (timeoutId !== null) {
        clearTimeout(timeoutId as number)
    }
    return null
}

function refreshSessionTimeout(timeoutId: SessionTimeout | null) {
    clearSessionTimeout(timeoutId)
    return setTimeout(() => {
        setShowContinueSessionModal(true)
    }, EditSessionDuration.asMilliseconds() - EditSessionConfirmModalDuration.asMilliseconds())
}

async function prepareCartInstance(dto: IGetShippingOrderCartDto) {
    const units = unitsStore.$units.getState()
    const newCart = new ShippingOrderCart(dto, units)
    return newCart
}

// Exports
export default {
    $cart,
    $cartUpdatesInFlightCount,
    $isUpdateCartPending,
    $showContinueSessionModal,
    createCart,
    updateCart,
    touchCart,
    changeCartStatus,
    deleteCart,
    publishCart,
    addAssets,
    changeAssets,
    deleteAssets,
    createManualPositions,
    updateManualPositions,
    deleteManualPosition,
    createTransport,
    updateTransport,
    deleteTransport,
    updateSegment,
    moveSegment,
    importLoadingList,
    exportLoadingList,
    downloadPickingList,
    getRequestLocations,
    createRequestLocation,
    reset,
}
